// types
import { Project, ReconciliationItem, ReconciliationItemStatus } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Chip, Grid, Typography } from '@mui/material';

// ui
import { BtnMenu } from '../../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { MenuItemProps } from '../../../ui/Menu/Menu';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';
import { Time } from '../../../ui/Time/Time';

// dialogs
import { DialogEditReconciliationItem } from '../../../dialogs/DialogEditReconciliationItem/DialogEditReconciliationItem';
import { DialogUpdateReconciliationItemStatus } from '../../../dialogs/DialogUpdateReconciliationItemStatus/DialogUpdateReconciliationItemStatus';

// icons
import {
	DoDisturbOutlined as IconDoDisturb,
	ModeEditOutlined as IconModeEdit,
	PowerSettingsNew as IconPowerSettingsNew,
} from '@mui/icons-material';
export interface TableBasicReconciliationItemsProps extends Omit<TableProps, 'headers'> {
	data: ReconciliationItem[];
	refetchReconciliationItems?: () => void;
}

export const TableBasicReconciliationItems = ({
	data,
	refetchReconciliationItems,
	...props
}: TableBasicReconciliationItemsProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');

	// handlers
	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogReconciliationItem');
		searchParams.delete('dialogStatus');
		setSearchParams(searchParams);
	};

	const handleEditReconciliationItem = (reconciliationItem: ReconciliationItem) => {
		searchParams.set('dialog', 'editReconciliationItem');
		searchParams.set('dialogReconciliationItem', String(reconciliationItem.id));
		setSearchParams(searchParams);
	};

	// headers
	const headers: TableHeader[] = [
		{
			label: t('name'),
			render: (name: string) => {
				return (
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography variant="body1">{name}</Typography>
						</Grid>
					</Grid>
				);
			},
			value: 'name',
		},
		{
			label: t('type'),
			render: (project: Project, header: TableHeader, reconciliationItem: ReconciliationItem) => {
				return (
					<Chip
						color={reconciliationItem.isDefault ? 'info' : 'purple'}
						label={reconciliationItem.isDefault ? 'World Bank' : t('project')}
						size="small"
						variant="twoTone"
					/>
				);
			},
			value: 'project',
		},
		{
			label: t('status'),
			render: (status: ReconciliationItemStatus) => {
				return <ChipStatus status={status} />;
			},
			value: 'status',
		},
		{
			label: t('created'),
			render: (created: string) => {
				return <Time date={created} />;
			},
			value: 'created',
		},
		{
			align: 'right',
			label: '',
			render: (created: string, header: TableHeader, reconciliationItem: ReconciliationItem) => {
				const btnMenuItems: MenuItemProps[] = [];

				if (!reconciliationItem.isDefault) {
					btnMenuItems.push({
						icon: <IconModeEdit />,
						children: t('edit'),
						onClick: () => handleEditReconciliationItem(reconciliationItem),
					});
					// update status - only for project-specific items
					const isActive = reconciliationItem.status === 'ACTIVE';
					const newStatus: ReconciliationItemStatus = isActive ? 'DEACTIVATED' : 'ACTIVE';
					btnMenuItems.push({
						icon: isActive ? <IconDoDisturb /> : <IconPowerSettingsNew />,
						children: isActive ? t('deactivate') : t('activate'),
						onClick: () => {
							searchParams.set('dialog', 'updateReconciliationItemStatus');
							searchParams.set('dialogReconciliationItem', String(reconciliationItem.id));
							searchParams.set('dialogStatus', newStatus);
							setSearchParams(searchParams);
						},
					});
				}

				if (btnMenuItems.length) {
					return <BtnMenu items={btnMenuItems} />;
				}
			},
			value: 'created',
		},
	];

	return (
		<>
			<Table {...props} headers={headers} data={data} />
			<DialogEditReconciliationItem
				isOpen={paramDialog === 'editReconciliationItem'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchReconciliationItems) {
						refetchReconciliationItems();
					}
				}}
			/>
			<DialogUpdateReconciliationItemStatus
				isOpen={paramDialog === 'updateReconciliationItemStatus'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchReconciliationItems) {
						refetchReconciliationItems();
					}
				}}
			/>
		</>
	);
};
